import React, { useState, useCallback, useMemo } from 'react';
import {
  ReactFlow,
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  NodeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Box } from '@mui/material';
import PipelineStepNode from './PipelineStepNode.tsx';
import StepDetailModal from './StepDetailModal.tsx';
import { PipelineNodeData, mapUpdateTypeToStatus, getStepMessage } from '../types/pipeline.ts';

// Define the 7 pipeline steps layout (horizontal flow)
const initialNodes: Node<PipelineNodeData>[] = [
  {
    id: '1',
    type: 'pipelineStep',
    position: { x: 0, y: 100 },
    data: {
      step: 1,
      name: 'Select Images',
      status: 'pending',
    },
  },
  {
    id: '2',
    type: 'pipelineStep',
    position: { x: 280, y: 100 },
    data: {
      step: 2,
      name: 'Download Images',
      status: 'pending',
    },
  },
  {
    id: '3',
    type: 'pipelineStep',
    position: { x: 560, y: 100 },
    data: {
      step: 3,
      name: 'Get Model Config',
      status: 'pending',
    },
  },
  {
    id: '4',
    type: 'pipelineStep',
    position: { x: 840, y: 100 },
    data: {
      step: 4,
      name: 'Register Batch',
      status: 'pending',
    },
  },
  {
    id: '5',
    type: 'pipelineStep',
    position: { x: 0, y: 280 },
    data: {
      step: 5,
      name: 'Bulk Predict',
      status: 'pending',
    },
  },
  {
    id: '6',
    type: 'pipelineStep',
    position: { x: 280, y: 280 },
    data: {
      step: 6,
      name: 'Add Annotations',
      status: 'pending',
    },
  },
  {
    id: '7',
    type: 'pipelineStep',
    position: { x: 560, y: 280 },
    data: {
      step: 7,
      name: 'Extract Weeds',
      status: 'pending',
    },
  },
];

const initialEdges: Edge[] = [
  { id: 'e1-2', source: '1', target: '2', animated: false },
  { id: 'e2-3', source: '2', target: '3', animated: false },
  { id: 'e3-4', source: '3', target: '4', animated: false },
  { id: 'e4-5', source: '4', target: '5', type: 'smoothstep', animated: false },
  { id: 'e5-6', source: '5', target: '6', animated: false },
  { id: 'e6-7', source: '6', target: '7', animated: false },
];

interface PipelineFlowProps {
  onUpdateFromStream?: (update: any) => void;
}

const PipelineFlow: React.FC<PipelineFlowProps> = ({ onUpdateFromStream }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState<PipelineNodeData | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Register custom node type
  const nodeTypes: NodeTypes = useMemo(
    () => ({
      pipelineStep: PipelineStepNode,
    }),
    []
  );

  // Handle node click
  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node<PipelineNodeData>) => {
    setSelectedNode(node.data);
    setModalOpen(true);
  }, []);

  // Handle modal close
  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  // Update node data from gRPC stream
  const updateNodeFromStream = useCallback((update: any) => {
    const currentStep = update.currentStep;
    const updateType = update.updateType;
    const newStatus = mapUpdateTypeToStatus(updateType);
    const message = getStepMessage(update);

    setNodes((nds) =>
      nds.map((node) => {
        if (node.data.step === currentStep) {
          const updatedData: PipelineNodeData = {
            ...node.data,
            status: newStatus,
            message: message,
            progress: update.progressPercentage,
          };

          // Set start time when step starts
          if (updateType === 1 && !node.data.startTime) {
            updatedData.startTime = Date.now();
          }

          // Set duration when step completes
          // Check both updatedData and node.data for startTime to handle React batched updates
          if (updateType === 3) {
            const startTime = updatedData.startTime || node.data.startTime;
            if (startTime) {
              updatedData.duration = Math.floor((Date.now() - startTime) / 1000);
            }
          }

          // Update step-specific details
          if (!updatedData.details) {
            updatedData.details = {};
          }

          // Step 1: Select Images
          if (currentStep === 1) {
            if (update.selectImagesMessage) {
              // Try to extract image count from message
              const match = update.selectImagesMessage.match(/(\d+)\s+images?/i);
              if (match) {
                updatedData.details.imageCount = parseInt(match[1]);
              }
            }
            if (update.selectImagesS3Uri) {
              updatedData.details.s3Uri = update.selectImagesS3Uri;
            }
          }

          // Step 2: Download
          if (currentStep === 2) {
            if (update.downloadedImagesPath) {
              updatedData.details.downloadPath = update.downloadedImagesPath;
            }
            if (update.downloadedImagesCount) {
              updatedData.details.downloadedCount = update.downloadedImagesCount;
            }
          }

          // Step 3: Model Config
          if (currentStep === 3) {
            if (update.modelName) {
              updatedData.details.modelName = update.modelName;
            }
            if (update.classesNumber) {
              updatedData.details.classesNumber = update.classesNumber;
            }
          }

          // Step 4: Register Batch
          if (currentStep === 4) {
            if (update.batchId) {
              updatedData.details.batchId = update.batchId;
            }
            if (update.registerBatchMessage) {
              updatedData.message = update.registerBatchMessage;
            }
          }

          // Step 5: Bulk Predict
          if (currentStep === 5) {
            console.log('[PipelineFlow] Processing Step 5 update:', {
              hasProcessedCount: !!update.bulkPredictProcessedCount,
              hasBatchId: !!update.batchId,
              hasSeedOutputUrl: !!update.seedOutputUrl,
              seedOutputUrl: update.seedOutputUrl
            });

            if (update.bulkPredictProcessedCount) {
              updatedData.details.processedCount = update.bulkPredictProcessedCount;
            }
            if (update.batchId) {
              updatedData.details.batchId = update.batchId;
            }
            if (update.seedOutputUrl) {
              updatedData.details.seedOutputUrl = update.seedOutputUrl;
              console.log('[PipelineFlow] ✓ Added seedOutputUrl to node details:', update.seedOutputUrl);
            } else {
              console.warn('[PipelineFlow] ⚠️ No seedOutputUrl in update for Step 5!');
            }

            console.log('[PipelineFlow] Final updatedData.details:', updatedData.details);
          }

          // Step 6: Add Annotations
          if (currentStep === 6) {
            if (update.batchId) {
              updatedData.details.batchId = update.batchId;
            }
            if (update.batchInsightsCount) {
              updatedData.details.annotationCount = update.batchInsightsCount;
            }
          }

          // Step 7: Extract Weed Instances
          if (currentStep === 7) {
            if (update.highWeedImagesCount) {
              updatedData.details.highWeedCount = update.highWeedImagesCount;
            }
            if (update.weedInstancesExtracted) {
              updatedData.details.instancesExtracted = update.weedInstancesExtracted;
            }
            if (update.weedExtractsS3Uri) {
              updatedData.details.weedExtractsUri = update.weedExtractsS3Uri;
            }
          }

          return {
            ...node,
            data: updatedData,
          };
        }
        return node;
      })
    );

    // Animate edge when step is active
    setEdges((eds) =>
      eds.map((edge) => ({
        ...edge,
        animated: edge.source === currentStep.toString() && updateType !== 3,
        style: {
          stroke: edge.source === currentStep.toString() && updateType !== 3 ? '#FF9800' : '#b1b1b7',
          strokeWidth: edge.source === currentStep.toString() && updateType !== 3 ? 3 : 2,
        },
      }))
    );

    // Call the optional callback
    if (onUpdateFromStream) {
      onUpdateFromStream(update);
    }
  }, [setNodes, setEdges, onUpdateFromStream]);

  // Expose the update function to parent components
  React.useImperativeHandle(
    React.useRef(),
    () => ({
      updateFromStream: updateNodeFromStream,
    }),
    [updateNodeFromStream]
  );

  // Store the update function in a ref so parent can access it
  React.useEffect(() => {
    // Attach the update function to the component instance
    (window as any).pipelineFlowUpdate = updateNodeFromStream;

    return () => {
      delete (window as any).pipelineFlowUpdate;
    };
  }, [updateNodeFromStream]);

  return (
    <Box sx={{ width: '100%', height: '500px', border: '1px solid #e0e0e0', borderRadius: 2 }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        nodeTypes={nodeTypes}
        fitView
        attributionPosition="bottom-left"
        minZoom={0.5}
        maxZoom={1.5}
        defaultEdgeOptions={{
          style: { stroke: '#b1b1b7', strokeWidth: 2 },
        }}
      >
        <Background color="#f5f5f5" gap={16} />
        <Controls />
        <MiniMap
          nodeColor={(node) => {
            const data = node.data as PipelineNodeData;
            switch (data.status) {
              case 'pending':
                return '#9E9E9E';
              case 'in_progress':
                return '#FF9800';
              case 'completed':
                return '#4CAF50';
              case 'error':
                return '#F44336';
              default:
                return '#9E9E9E';
            }
          }}
          maskColor="rgba(0, 0, 0, 0.1)"
        />
      </ReactFlow>

      {/* Detail Modal */}
      <StepDetailModal open={modalOpen} onClose={handleCloseModal} stepData={selectedNode} />
    </Box>
  );
};

// Create a wrapper component with ref forwarding
export interface PipelineFlowHandle {
  updateFromStream: (update: any) => void;
  reset: () => void;
}

export const PipelineFlowWithRef = React.forwardRef<PipelineFlowHandle, PipelineFlowProps>(
  (props, ref) => {
    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
    const [selectedNode, setSelectedNode] = useState<PipelineNodeData | null>(null);
    const [modalOpen, setModalOpen] = useState(false);

    // Use ref to store start times outside React state to avoid batching issues
    const stepStartTimes = React.useRef<{ [key: number]: number }>({});

    const nodeTypes: NodeTypes = useMemo(
      () => ({
        pipelineStep: PipelineStepNode,
      }),
      []
    );

    const onNodeClick = useCallback((_event: React.MouseEvent, node: Node<PipelineNodeData>) => {
      setSelectedNode(node.data);
      setModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
      setModalOpen(false);
    }, []);

    const updateNodeFromStream = useCallback((update: any) => {
      const currentStep = update.currentStep;
      const updateType = update.updateType;
      const newStatus = mapUpdateTypeToStatus(updateType);
      const message = getStepMessage(update);

      // Track start time in ref when step starts OR on first update for this step
      if (updateType === 1 || (updateType === 2 && !stepStartTimes.current[currentStep])) {
        if (!stepStartTimes.current[currentStep]) {
          stepStartTimes.current[currentStep] = Date.now();
          console.log(`[PipelineFlow] Step ${currentStep} started at ${new Date().toISOString()}`);
        }
      }

      setNodes((nds) =>
        nds.map((node) => {
          if (node.data.step === currentStep) {
            const updatedData: PipelineNodeData = {
              ...node.data,
              status: newStatus,
              message: message,
              progress: update.progressPercentage,
            };

            // Set startTime from ref
            if (stepStartTimes.current[currentStep]) {
              updatedData.startTime = stepStartTimes.current[currentStep];
            }

            // Calculate duration when step completes
            if (updateType === 3 && stepStartTimes.current[currentStep]) {
              const duration = Math.floor((Date.now() - stepStartTimes.current[currentStep]) / 1000);
              updatedData.duration = duration;
              console.log(`[PipelineFlow] Step ${currentStep} completed. Duration: ${duration}s`);
            }

            if (!updatedData.details) {
              updatedData.details = {};
            }

            // Update step-specific details based on current step
            if (currentStep === 1) {
              console.log('[PipelineFlow] Step 1 update received. All fields:', Object.keys(update));
              console.log('[PipelineFlow] Step 1 update.workspaceDirectoryUrl:', update.workspaceDirectoryUrl);

              if (update.selectImagesMessage) {
                const match = update.selectImagesMessage.match(/(\d+)\s+images?/i);
                if (match) {
                  updatedData.details.imageCount = parseInt(match[1]);
                }
              }
              if (update.selectImagesS3Uri) updatedData.details.s3Uri = update.selectImagesS3Uri;
              // Capture workspace directory URL as soon as it's received (during progress or completion)
              if (update.workspaceDirectoryUrl) {
                updatedData.details.workspaceDirectoryUrl = update.workspaceDirectoryUrl;
                console.log('[PipelineFlow] ✓ Captured workspaceDirectoryUrl for Step 1 (images):', update.workspaceDirectoryUrl);
              } else {
                console.log('[PipelineFlow] ⚠️  workspaceDirectoryUrl NOT in update for Step 1');
              }
            } else if (currentStep === 2) {
              if (update.downloadedImagesPath) updatedData.details.downloadPath = update.downloadedImagesPath;
              if (update.downloadedImagesCount) updatedData.details.downloadedCount = update.downloadedImagesCount;
            } else if (currentStep === 3) {
              if (update.modelName) updatedData.details.modelName = update.modelName;
              if (update.classesNumber) updatedData.details.classesNumber = update.classesNumber;
              // Set a completion message if completed and no specific message
              if (updateType === 3 && !update.modelConfigMessage) {
                updatedData.message = 'Model configuration retrieved';
              }
            } else if (currentStep === 4) {
              if (update.batchId) updatedData.details.batchId = update.batchId;
              if (update.registerBatchMessage) updatedData.message = update.registerBatchMessage;
            } else if (currentStep === 5) {
              if (update.bulkPredictProcessedCount) updatedData.details.processedCount = update.bulkPredictProcessedCount;
              if (update.batchId) updatedData.details.batchId = update.batchId;
              // Capture seedOutputUrl as soon as it's received (during progress or completion)
              if (update.seedOutputUrl) {
                updatedData.details.seedOutputUrl = update.seedOutputUrl;
                console.log('[PipelineFlow] ✓ Captured seedOutputUrl (available immediately):', update.seedOutputUrl);
              }
            } else if (currentStep === 6) {
              if (update.batchId) updatedData.details.batchId = update.batchId;
              if (update.batchInsightsCount) updatedData.details.annotationCount = update.batchInsightsCount;
            } else if (currentStep === 7) {
              if (update.highWeedImagesCount) updatedData.details.highWeedCount = update.highWeedImagesCount;
              if (update.weedInstancesExtracted) updatedData.details.instancesExtracted = update.weedInstancesExtracted;
              if (update.weedExtractsS3Uri) updatedData.details.weedExtractsUri = update.weedExtractsS3Uri;
              // Capture workspace directory URL as soon as it's received (during progress or completion)
              if (update.workspaceDirectoryUrl) {
                updatedData.details.workspaceDirectoryUrl = update.workspaceDirectoryUrl;
                console.log('[PipelineFlow] ✓ Captured workspaceDirectoryUrl for Step 7 (weed instances):', update.workspaceDirectoryUrl);
              }
            }

            return { ...node, data: updatedData };
          }
          return node;
        })
      );

      setEdges((eds) =>
        eds.map((edge) => ({
          ...edge,
          animated: edge.source === currentStep.toString() && updateType !== 3,
          style: {
            stroke: edge.source === currentStep.toString() && updateType !== 3 ? '#FF9800' : '#b1b1b7',
            strokeWidth: edge.source === currentStep.toString() && updateType !== 3 ? 3 : 2,
          },
        }))
      );

      if (props.onUpdateFromStream) {
        props.onUpdateFromStream(update);
      }
    }, [setNodes, setEdges, props]);

    // Reset function to clear start times and reset nodes
    const resetPipeline = useCallback(() => {
      stepStartTimes.current = {};
      setNodes(initialNodes);
      setEdges(initialEdges);
      console.log('[PipelineFlow] Pipeline reset - cleared start times and reset nodes');
    }, [setNodes, setEdges]);

    React.useImperativeHandle(ref, () => ({
      updateFromStream: updateNodeFromStream,
      reset: resetPipeline,
    }));

    return (
      <Box sx={{ width: '100%', height: '550px', border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          fitView
          attributionPosition="bottom-left"
          minZoom={0.5}
          maxZoom={1.5}
          defaultEdgeOptions={{
            style: { stroke: '#b1b1b7', strokeWidth: 2 },
          }}
        >
          <Background color="#f5f5f5" gap={16} />
          <Controls />
          <MiniMap
            nodeColor={(node) => {
              const data = node.data as PipelineNodeData;
              switch (data.status) {
                case 'pending':
                  return '#9E9E9E';
                case 'in_progress':
                  return '#FF9800';
                case 'completed':
                  return '#4CAF50';
                case 'error':
                  return '#F44336';
                default:
                  return '#9E9E9E';
              }
            }}
            maskColor="rgba(0, 0, 0, 0.1)"
          />
        </ReactFlow>

        <StepDetailModal open={modalOpen} onClose={handleCloseModal} stepData={selectedNode} />
      </Box>
    );
  }
);

PipelineFlowWithRef.displayName = 'PipelineFlowWithRef';

export default PipelineFlow;
